/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */

import { Result } from '../../utility/result.js';
import { TypeBrand } from './type-brand.js';

function GenericInteger(signed: boolean, bits: number) {
  function validate(value: number): string | null {
    if(Number.isSafeInteger(value) === false) {
      return `number {${value}} is not a safe integer`;
    }

    if(signed) {
      const min = -(2 ** (bits - 1));
      const high = (2 ** (bits - 1)) - 1;
      if(value < min || value > high) {
        return `number {${value}} is out of range`;
      }
    }
    else {
      const min = 0;
      const high = (2 ** bits) - 1;
      if(value < min || value > high) {
        return `number {${value}} is out of range`;
      }
    }

    return null;
  }

  class BoundedInteger {
    public constructor(
      public readonly __value: number,
    ) {}

    static make(value: number): BoundedInteger {
      const error = validate(value);
      if(error !== null) {
        throw new Error(error);
      }

      return new BoundedInteger(value);
    }

    static makeOrNull(value: number): BoundedInteger | null {
      const error = validate(value);
      if(error !== null) {
        return null;
      }

      return new BoundedInteger(value);
    }

    static makeSafe(value: number): Result<BoundedInteger, string> {
      const error = validate(value);
      if(error !== null) {
        return Result.error(error);
      }

      return Result.ok(new BoundedInteger(value));
    }

    asNumber(): number {
      return this.__value;
    }
  }

  return BoundedInteger;
}

export interface u8 {
  __u8Brand: TypeBrand;
}

export class u8 extends GenericInteger(false, 8) {
  static make(value: number): u8 {
    return super.make(value) as u8;
  }

  static makeOrNull(value: number): u8 | null {
    return super.makeOrNull(value) as u8 | null;
  }

  static makeSafe(value: number): Result<u8, string> {
    return super.makeSafe(value) as Result<u8, string>;
  }
}

export interface u16 {
  __u16Brand: TypeBrand;
}

export class u16 extends GenericInteger(false, 16) {
  static make(value: number): u16 {
    return super.make(value) as u16;
  }

  static makeOrNull(value: number): u16 | null {
    return super.makeOrNull(value) as u16 | null;
  }

  static makeSafe(value: number): Result<u16, string> {
    return super.makeSafe(value) as Result<u16, string>;
  }
}

export interface u32 {
  __u32Brand: TypeBrand;
}

export class u32 extends GenericInteger(false, 32) {
  static make(value: number): u32 {
    return super.make(value) as u32;
  }

  static makeOrNull(value: number): u32 | null {
    return super.makeOrNull(value) as u32 | null;
  }

  static makeSafe(value: number): Result<u32, string> {
    return super.makeSafe(value) as Result<u32, string>;
  }
}

export interface i8 {
  __i8Brand: TypeBrand;
}

export class i8 extends GenericInteger(true, 8) {
  static make(value: number): i8 {
    return super.make(value) as i8;
  }

  static makeOrNull(value: number): i8 | null {
    return super.makeOrNull(value) as i8 | null;
  }

  static makeSafe(value: number): Result<i8, string> {
    return super.makeSafe(value) as Result<i8, string>;
  }
}

export interface i16 {
  __i16Brand: TypeBrand;
}

export class i16 extends GenericInteger(true, 16) {
  static make(value: number): i16 {
    return super.make(value) as i16;
  }

  static makeOrNull(value: number): i16 | null {
    return super.makeOrNull(value) as i16 | null;
  }

  static makeSafe(value: number): Result<i16, string> {
    return super.makeSafe(value) as Result<i16, string>;
  }
}

export interface i32 {
  __i32Brand: TypeBrand;
}

export class i32 extends GenericInteger(true, 32) {
  static make(value: number): i32 {
    return super.make(value) as i32;
  }

  static makeOrNull(value: number): i32 | null {
    return super.makeOrNull(value) as i32 | null;
  }

  static makeSafe(value: number): Result<i32, string> {
    return super.makeSafe(value) as Result<i32, string>;
  }
}
